import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import Button from "./ui/button";

const services = [
  {
    number: "01",
    title: "Marriage Coaching",
    description:
      "For married couples who desire to reconnect, strengthen communication, rebuild trust, deepen intimacy, and create a healthier relationship.",
    link: "/marriage-coaching",
  },
  {
    number: "02",
    title: "Relationship Coaching",
    description:
      "For individuals and couples who desire biblical guidance, clarity, healing, and practical tools for healthier relationships.",
    link: "/relationship-coaching",
  },
  {
    number: "03",
    title: "Premarital Counseling",
    description:
      "We believe marriage should be entered into with wisdom, prayer, preparation, and understanding. Our premarital counseling sessions help engaged couples build a strong foundation before saying 'I do.'",
    link: "/premarital-counseling",
  },
  {
    number: "04",
    title: "Nuptials & Wedding Officiating",
    description:
      "Maurice and Kim provide meaningful, faith-centered wedding ceremonies for couples seeking a Christ-centered celebration of marriage.",
    link: "/wedding-officiating",
  },
  {
    number: "05",
    title: "Virtual & In-Person Appointments",
    description:
      "We offer both in-person and virtual appointments to serve couples locally, across the United States, and internationally.",
    link: "/contact",
  },
  {
    number: "06",
    title: "Flexible Scheduling",
    description:
      "We understand life can be busy. Flexible appointment times are available for couples with demanding schedules.",
    link: "/contact",
  },
];

function ServiceRow({ number, title, description, topics, link }) {
  const controls = useAnimation();

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.15,
  });

  useEffect(() => {
    if (inView) {
      controls.start({
        opacity: 1,
        y: 0,
      });
    }
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      initial={{
        opacity: 0,
        y: 40,
      }}
      animate={controls}
      transition={{
        duration: 0.6,
      }}
      className="
        border-b
        border-[#E7DDD1]
        py-12
        group
      "
    >
      <div className="grid lg:grid-cols-[110px_1fr] gap-8">
        <div>
          <span className="text-6xl font-bold text-[#7A5C11]/25">{number}</span>
        </div>

        <div>
          <h3 className="text-3xl font-bold text-[#2A2A2A] mb-4">{title}</h3>

          <p className="text-lg text-[#555555] leading-relaxed max-w-3xl">
            {description}
          </p>

          {topics && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-8">
              {topics.map((topic) => (
                <div
                  key={topic}
                  className="
                    px-3
                    py-2
                    rounded-full
                    bg-[#D4AF37]/10
                    text-[#7A5C11]
                    text-sm
                    font-medium
                    text-center
                  "
                >
                  {topic}
                </div>
              ))}
            </div>
          )}

          <Link
            to={link}
            className="
              inline-flex
              items-center
              gap-2
              mt-8
              font-semibold
              text-[#7A5C11]
              hover:gap-4
              transition-all
            "
          >
            Learn More →
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

export default function Services() {
  const navigate = useNavigate();

  return (
    <section id="services" className="relative py-24 px-6 overflow-hidden">
      {/* Gold Glow */}
      <div
        className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-3xl opacity-10"
        style={{
          background: "#D4AF37",
        }}
      />
      {/* Cream Glow */}
      <div
        className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full blur-3xl opacity-10"
        style={{
          background: "#EADCC8",
        }}
      />
      <div className="relative z-10">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-20">
          <p className="uppercase tracking-[0.35em] text-[#555555] text-xl font-semibold mb-4">
            COACHING & COUNSELING SERVICES
          </p>

          <h2 className="text-5xl md:text-6xl font-bold text-[#2A2A2A] leading-tight mb-6">
            Helping Individuals, Couples & Families Flourish
          </h2>

          <p className="text-lg md:text-xl text-[#555555] leading-relaxed">
            Through biblical counseling, marriage coaching, relationship
            guidance, and faith-centered support, we help individuals and
            couples build stronger relationships, healthier communication, and
            lasting hope.
          </p>
        </div>

        {/* CTA */}
        <div className="flex justify-center mb-16">
          <Button
            onClick={() => navigate("/contact")}
            className="
          bg-[#D4AF37]
          hover:bg-[#C39A2D]
          text-white
          px-8
          py-6
          rounded-full
          text-lg
          font-semibold
          shadow-lg
          transition-all
          duration-300
          hover:scale-105
        "
          >
            Schedule a Consultation
          </Button>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {services.map((service) => (
            <ServiceRow key={service.number} {...service} />
          ))}
        </div>
      </div>
    </section>
  );
}
